import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUser, useAuth } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

const CODE_EXPIRY_SECONDS = 600; // 10 minutes (Clerk default)
const MAX_ATTEMPTS = 3;

export function VerifyCode() {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(MAX_ATTEMPTS);
  const [timeLeft, setTimeLeft] = useState(CODE_EXPIRY_SECONDS);
  const [locked, setLocked] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { getToken } = useAuth();
  const { toast } = useToast();

  const state = location.state as { email?: string; emailAddressId?: string } | null;
  const email = state?.email || "";
  const emailAddressId = state?.emailAddressId || "";

  // Redirect if no email in state
  useEffect(() => {
    if (!email || !emailAddressId) {
      navigate("/verification/school-email");
    }
  }, [email, emailAddressId, navigate]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft <= 0 || locked) return;
    const timer = setInterval(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearInterval(timer);
  }, [timeLeft, locked]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (locked || timeLeft <= 0 || code.length !== 6 || !user) return;

    setLoading(true);
    try {
      console.log('[FRONTEND] Attempting verification with code...');

      // Find the email address object
      const emailAddress = user.emailAddresses.find(e => e.id === emailAddressId);

      if (!emailAddress) {
        throw new Error("Email address not found");
      }

      // Verify code via Clerk
      await emailAddress.attemptVerification({ code });

      console.log('[FRONTEND] Code verified successfully by Clerk');

      // Reload user to get updated email addresses
      await user.reload();

      console.log('[FRONTEND] Calling backend to update database...');

      // Now call backend to update database
      const token = await getToken();
      const apiUrl = import.meta.env.VITE_API_URL || "";
      const res = await fetch(`${apiUrl}/api/verification/verify-code`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emailAddressId }),
      });

      const data = await res.json();

      if (res.ok) {
        console.log('[FRONTEND] Backend updated, premium unlocked:', data.premiumUnlocked);
        navigate("/verification/success", { state: { premiumUnlocked: data.premiumUnlocked } });
      } else {
        throw new Error(data.error || "Failed to update database");
      }
    } catch (error: any) {
      console.error('[FRONTEND] Verification error:', error);
      setCode("");
      const remaining = attemptsRemaining - 1;
      setAttemptsRemaining(remaining);

      if (remaining <= 0) {
        setLocked(true);
        toast({
          title: "Too Many Attempts",
          description: "Request a new code to continue.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Invalid Code",
          description: error?.errors?.[0]?.message || error?.message || `${remaining} attempt${remaining !== 1 ? "s" : ""} remaining`,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = useCallback(async () => {
    setResending(true);
    try {
      if (!user) {
        throw new Error("You must be logged in");
      }

      console.log('[FRONTEND] Resending verification code...');

      // Find the email address object
      const emailAddress = user.emailAddresses.find(e => e.id === emailAddressId);

      if (!emailAddress) {
        throw new Error("Email address not found");
      }

      // Trigger new verification code
      await emailAddress.prepareVerification({ strategy: 'email_code' });

      console.log('[FRONTEND] New code sent successfully');

      setTimeLeft(CODE_EXPIRY_SECONDS);
      setAttemptsRemaining(MAX_ATTEMPTS);
      setLocked(false);
      setCode("");
      toast({
        title: "Code Sent",
        description: "A new verification code has been sent to your email.",
      });
    } catch (error: any) {
      console.error('[FRONTEND] Resend error:', error);
      toast({
        title: "Error",
        description: error?.errors?.[0]?.message || error?.message || "Failed to resend code.",
        variant: "destructive",
      });
    } finally {
      setResending(false);
    }
  }, [user, emailAddressId, toast]);

  const isExpired = timeLeft <= 0;
  const canVerify = !locked && !isExpired && code.length === 6;

  return (
    <main
      className="min-h-screen bg-gradient-to-b from-bumble-yellow/20 to-background flex items-center justify-center p-4"
      role="main"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-bumble-dark">Enter Verification Code</CardTitle>
          <CardDescription>
            Enter the 6-digit code sent to {email}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {locked ? (
            <div className="text-center space-y-4">
              <p className="text-destructive font-medium" role="alert">
                Too many attempts. Request a new code.
              </p>
              <Button
                onClick={handleResendCode}
                disabled={resending}
                className="bg-bumble-yellow text-bumble-dark hover:bg-bumble-yellow/90"
              >
                {resending ? "Sending..." : "Request New Code"}
              </Button>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="code" className="sr-only">
                  Verification code
                </Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="text-center text-2xl tracking-widest font-mono"
                  autoFocus
                  autoComplete="one-time-code"
                  aria-describedby="code-timer"
                  disabled={isExpired}
                />
              </div>

              <p
                id="code-timer"
                className={`text-sm text-center ${isExpired ? "text-destructive" : "text-muted-foreground"}`}
              >
                {isExpired
                  ? "Code expired. Request a new one."
                  : `Code expires in ${formatTime(timeLeft)}`}
              </p>

              <Button
                type="submit"
                className="w-full bg-bumble-yellow text-bumble-dark hover:bg-bumble-yellow/90"
                disabled={loading || !canVerify}
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>

              <div className="text-center">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleResendCode}
                  disabled={resending}
                  className="text-sm"
                >
                  {resending ? "Sending..." : "Resend Code"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
